<template>
  <div class="auth-shell">
    <div class="auth-card surface-elevated">
      <div class="brand-wrap">
        <img class="brand-logo" src="@/assets/images/logo.png" alt="BestPhone" />
      </div>
      <div class="stack-sm auth-header">
        <h1 class="display-title">{{ get_word('login_title') || 'התחברות לחשבון' }}</h1>
        <p class="muted">{{ get_word('login_subtitle') || 'איזה כיף שחזרתם. מתחברים וממשיכים בדיוק מאיפה שעצרתם.' }}</p>
      </div>

      <!-- 2 כפתורי טאב: סיסמה | קוד אישי -->
      <div class="login-switch" role="tablist">
        <button type="button" class="switch-btn" :class="{ active: loginMode === 'password' }" @click="setMode('password')">
          {{ get_word('login_password') || 'סיסמה' }}
        </button>
        <button type="button" class="switch-btn" :class="{ active: loginMode === 'otp' }" @click="setMode('otp')">
          {{ get_word('login_otp') || 'קוד אישי' }}
        </button>
      </div>

      <!-- טאב 1: מייל + סיסמה -->
      <form v-if="loginMode === 'password'" @submit.prevent="submitLoginPassword" class="auth-form stack-md">
        <div class="field">
          <label class="form-label">{{ get_word('email') || 'דואר אלקטרוני' }}</label>
          <input v-model="passwordForm.username" type="text" class="input-control"
            :placeholder="get_word('email_placeholder') || 'name@example.com'"
            @keyup.enter="submitLoginPassword" required />
          <small v-if="errors.username" class="help-error">{{ errors.username }}</small>
        </div>

        <div class="field">
          <label class="form-label">{{ get_word('password') || 'סיסמה' }}</label>
          <div class="password-field">
            <input v-model="passwordForm.password" :type="showPassword ? 'text' : 'password'"
              class="input-control" :placeholder="get_word('password_placeholder') || 'הכנס סיסמה'"
              @keyup.enter="submitLoginPassword" required />
            <button type="button" class="password-toggle" @click="showPassword = !showPassword">
              {{ showPassword ? 'הסתר' : 'הצג' }}
            </button>
          </div>
          <small v-if="errors.password" class="help-error">{{ errors.password }}</small>
        </div>

        <label class="check-row" for="remember_me">
          <input v-model="passwordForm.remember_me" type="checkbox" id="remember_me" />
          <span>{{ get_word('remember_me') || 'להישאר מחובר/ת' }}</span>
        </label>

        <button type="submit" :disabled="loading" class="btn btn-primary auth-submit">
          <span v-if="!loading">{{ get_word('login') || 'כניסה' }}</span>
          <span v-else>טוען...</span>
        </button>
        <div v-if="generalError" class="error-inline">{{ generalError }}</div>
      </form>

      <!-- טאב 2: קוד אישי למייל (מזהה: טלפון או מייל) -->
      <form v-else-if="loginMode === 'otp'" @submit.prevent="submitOtpFlow" class="auth-form stack-md">
        <div class="field">
          <label class="form-label">{{ get_word('email_or_phone') || 'מספר טלפון או כתובת מייל' }}</label>
          <input v-model="otpForm.identifier" type="text" class="input-control"
            placeholder="example@mail.com או 05XXXXXXXX" required />
          <small v-if="errors.identifier" class="help-error">{{ errors.identifier }}</small>
        </div>

        <div v-if="otpSent" class="field">
          <label class="form-label">{{ get_word('otp_code') || 'קוד אימות (6 ספרות)' }}</label>
          <input v-model="otpForm.code" type="text" class="input-control"
            maxlength="6" placeholder="123456" required />
          <small v-if="otpHint" class="otp-hint">{{ otpHint }}</small>
          <small v-if="errors.code" class="help-error">{{ errors.code }}</small>
        </div>

        <div class="otp-actions">
          <button v-if="!otpSent" type="button" :disabled="loading"
            class="btn btn-primary auth-submit" @click="sendOtp">
            <span v-if="!loading">שלח קוד</span>
            <span v-else>טוען...</span>
          </button>
          <template v-else>
            <button type="submit" :disabled="loading" class="btn btn-primary auth-submit">
              <span v-if="!loading">אימות וכניסה</span>
              <span v-else>טוען...</span>
            </button>
            <button type="button" :disabled="loading" class="btn btn-ghost auth-submit" @click="sendOtp">
              שליחה חוזרת
            </button>
          </template>
        </div>
        <div v-if="generalError" class="error-inline">{{ generalError }}</div>
      </form>

      <div class="auth-footer">
        <router-link to="/forgot-password" class="footer-link">{{ get_word('forgot_password') || 'שכחת סיסמה?' }}</router-link>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Login',
  data() {
    return {
      loginMode: 'password',

      passwordForm: { username: '', password: '', remember_me: false },

      otpForm: { identifier: '', code: '' },
      otpSent: false,
      otpHint: '',

      loading: false,
      showPassword: false,
      errors: {},
      generalError: ''
    }
  },
  mounted() {
    if (!this.isNull(localStorage.getItem('user_token'))) {
      this.$router.push('/');
    }
  },
  methods: {

    handleAuthSuccess(response) {
      if (!(response && response.data && response.data.token && response.data.user)) {
        return false
      }

      this.set_user_token(response.data.token)
      this.$store.commit('setUser', { user: response.data.user })

      if (response.data.requires_password_setup) {
        localStorage.setItem('require_password_setup', '1')
      } else {
        localStorage.removeItem('require_password_setup')
      }

      this.activateSuccess('מעולה! ההתחברות בוצעה בהצלחה')
      setTimeout(() => this.$router.push('/'), 500)
      return true
    },

    setMode(mode) {
      this.loginMode = mode;
      this.errors = {};
      this.generalError = '';
      this.otpSent = false;
      this.otpHint = '';
      this.otpForm.code = '';
    },

    submitLoginPassword() {
      this.errors = {};
      this.generalError = '';
      if (!this.passwordForm.username) { this.errors.username = 'יש להזין כתובת מייל או שם משתמש'; return; }
      if (!this.passwordForm.password) { this.errors.password = 'יש להזין סיסמה'; return; }

      this.loading = true;
      this.api({
        action: 'login',
        data: {
          username: this.passwordForm.username,
          password: this.passwordForm.password,
          remember_me: this.passwordForm.remember_me ? '1' : '0'
        },
        method: 'post'
      }, (response) => {
        if (this.handleAuthSuccess(response)) return
        this.generalError = 'ההתחברות לא הצליחה. כדאי לבדוק שוב את הפרטים.';
        this.loading = false;
      }, () => {
        this.generalError = 'קרתה שגיאה בהתחברות. נסו שוב בעוד רגע.';
        this.loading = false;
      });
    },

    sendOtp() {
      this.errors = {};
      this.generalError = '';
      if (!this.otpForm.identifier) { this.errors.identifier = 'יש להזין מספר טלפון או כתובת מייל'; return; }

      this.loading = true;
      this.api({
        action: 'send_login_otp',
        data: { identifier: this.otpForm.identifier },
        method: 'post'
      }, (response) => {
        if (response && response.data && response.data.success) {
          this.otpSent = true;
          this.otpHint = 'הקוד נשלח בהצלחה. בדקו את תיבת הדואר שלכם.';
          this.loading = false;
          return;
        }
        
        let error = 'לא הצלחנו לשלוח קוד כרגע. כדאי לוודא שהפרטים קיימים במערכת.';
        if (response && response.data && response.data.error) {
          if (response.data.error === 'user_not_found') {
            error = 'לא הצלחנו למצוא משתמש עם הפרטים האלה. בדוק שנית או נסה עם פרט אחר.';
          } else if (response.data.error === 'user_has_no_email') {
            error = 'למשתמש זה אין כתובת דואר רשומה.';
          }
        }
        
        this.generalError = error;
        this.loading = false;
      }, () => {
        this.generalError = 'לא הצלחנו לשלוח קוד כרגע. נסו שוב בעוד רגע.';
        this.loading = false;
      });
    },

    submitOtpFlow() {
      this.errors = {};
      this.generalError = '';
      if (!this.otpForm.identifier) { this.errors.identifier = 'יש להזין מספר טלפון או כתובת מייל'; return; }
      if (!this.otpForm.code) { this.errors.code = 'יש להזין קוד אימות'; return; }

      this.loading = true;
      this.api({
        action: 'verify_login_otp',
        data: { identifier: this.otpForm.identifier, code: this.otpForm.code },
        method: 'post'
      }, (response) => {
        if (this.handleAuthSuccess(response)) return
        this.generalError = 'קוד האימות לא תקין או שפג תוקפו.';
        this.loading = false;
      }, () => {
        this.generalError = 'קוד האימות לא תקין או שפג תוקפו.';
        this.loading = false;
      });
    }
  }
}
</script>

<style scoped lang="scss">
.login-switch {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 1rem;
  background: #f4ebff;
  padding: 0.35rem;
  border-radius: 12px;
}

.switch-btn {
  border: 0;
  background: transparent;
  color: #6c6283;
  font-weight: 700;
  border-radius: 9px;
  min-height: 40px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.82rem;

  &.active {
    background: #fff;
    color: #8424e3;
    box-shadow: 0 2px 10px rgba(132, 36, 227, 0.15);
  }
}

.otp-actions { display: grid; gap: 0.65rem; }

.otp-hint {
  display: block;
  margin-top: 0.4rem;
  color: #6c6283;
  font-size: 0.84rem;
}
</style>
